import { PersonalInterest, TeacherRole, TeacherStatus } from 'config/teacherProfiles';
import { EVCLocation } from 'model/misc';
import { Subject } from 'model/Subjects';

import { ITeacherAvailabilityRange } from './AvailabilityConstraints';
import { Booking } from './Booking';
import { School } from './misc';

export interface IUser {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  permissions: IGrantedPermission[];
  roles: IUserRole[];
  isActive: boolean;
}

export interface ITeacher {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  emailSubscribed: boolean;
  departmentId?: string;
  personalInterests: PersonalInterest[];
  instructionLanguages: string[];
  skillIds: string[];
  path: string;
  role?: TeacherRole;
  status: TeacherStatus;
  profilePicturePath?: string;
}

export interface TeacherPatch {
  lastName?: string;
  firstName?: string;
  email?: string;
  emailSubscribed?: boolean;
  personalInterests?: PersonalInterest[];
  instructionLanguages?: string[];
  skillIds?: TeacherSkillIdReplace[];
  role?: TeacherRole;
}

export interface TeacherSkillIdReplace {
  op: 'replace';
  skillIds: string[];
}

export interface IGrantedPermission {
  permission: Permissions;
  resourceType: ResourceTypes;
  resourceId: string;
}

export interface IUserRole {
  role: IUserRoleType;
  resourceType: ResourceTypes;
  resourceId: string;
  id: string;
}

export enum IUserRoleType {
  THIRD_PARTY = 'thirdParty',
  INDEPENDENT_CONTRACTOR = 'independentContractor',
  STAFF = 'staff',
  OFFLINE_TEACHER = 'offlineTeacher',
  FLEXIBLE_STAFF = 'flexibleStaff',
  TENANT_ADMIN = 'tenantAdmin',
  CENTER_ADMIN = 'toCenterAdmin',
  FRANCHISE_ADMIN = 'franchiseAdmin',
  SCHEDULE_ADMIN = 'scheduleAdmin',
  OFFLINE_DEPARTMENT_ADMIN = 'offlineDepartmentAdmin',
  SCHOOL_ADMIN = 'toSchoolAdmin',
  SCHOOL_SUPER_ADMIN = 'schoolSuperAdmin',
  STUDENT_ADMIN = 'studentAdmin',
  STUDENT_INTEGRATOR = 'studentIntegrator',
  ASOT_INTEGRATOR = 'asotIntegrator',
}

export enum Permissions {
  VIEW_TEACHERS_PORTAL = 'ViewTeachersPortal',
  VIEW_OPS_PORTAL = 'ViewOpsPortal',
  VIEW_STUDENTS_PORTAL = 'ViewStudentsPortal',
  VIEW_OFFLINE_TEACHERS_DASHBOARD = 'ViewOfflineTeachersDashboard',
  CREATE_INVOICES = 'CreateInvoices',
  CREATE_TEACHER_PROFILE = 'CreateTeacherProfile',
  VIEW_TEACHERS = 'ViewTeachers',
  VIEW_ATTENDANCE = 'ViewAttendance',
  EDIT_TEACHER_ROLE = 'EditTeacherRole',
  VIEW_CLASS_RECORDS = 'ViewClassRecords',
  EDIT_CLASS_RECORDS_CENTER_STATUS = 'EditClassRecordsCenterStatus',
  EDIT_CLASS_RECORDS_SYSTEM_STATUS = 'EditClassRecordsSystemStatus',
  VIEW_INVOICES = 'ViewInvoices',
  REVIEW_INVOICES = 'ReviewInvoices',
  SUBMIT_INVOICES = 'SubmitInvoices',
  VIEW_AVAILABILITY = 'ViewAvailability',
  EDIT_AVAILABILITY = 'EditAvailability',
  EDIT_TEACHER_FIRST_NAME = 'EditTeacherFirstName',
  EDIT_TEACHER_LAST_NAME = 'EditTeacherLastName',
  EDIT_TEACHER_EMAIL = 'EditTeacherEmail',
  EDIT_TEACHER_SKILLS = 'EditTeacherSkills',
  EDIT_TEACHER_SUBJECTS = 'EditTeacherSubjects',
  EDIT_TEACHER_INSTRUCTION_LANGUAGES = 'EditTeacherInstructionLanguages',
  EDIT_TEACHER_PERSONAL_INTERESTS = 'EditTeacherPersonalInterests',
  EDIT_TEACHER_PATH = 'EditTeacherPath',
  VIEW_TEACHER_PAY_RATE = 'ViewTeacherPayRate',
  EDIT_TEACHER_PAY_RATE = 'EditTeacherPayRate',
  EDIT_TEACHER_EMAIL_SUBSCRIPTION = 'EditTeacherEmailSubscription',
  EDIT_TEACHER_ASOT_MEMBER_ID = 'EditTeacherAsotMemberID',
  SUBOUT_BOOKING = 'SubOutBooking',
  VIEW_BOOKING_EXIT_SURVEY = 'ViewBookingExitSurvey',
  VIEW_PEAK_RANGES = 'ViewPeakRanges',
  CREATE_BOOKING_BY_TEACHER = 'CreateBookingsByTeacher',
  CREATE_BLOCK = 'CreateBlock',
  VIEW_BLOCK = 'ViewBlock',
  DELETE_BLOCK = 'DeactivateBlock',
  VIEW_CLASSROOM_RECORDING = 'ViewClassroomRecording',
  UPDATE_BOOKING_EXIT_SURVEY = 'UpdateBookingExitSurvey',
  VIEW_UNCLAIMED_BOOKINGS = 'ViewUnclaimedBookings',
  CLAIM_UNCLAIMED_BOOKINGS = 'ClaimUnclaimedBookings',
  CHATROOM_CREATE = 'CreateChatRoom',
  FORCE_APPROVE_INVOICES = 'ForceApproveInvoices',
  VIEW_EXCLUSIVE_AVAILABILITY = 'ExclusiveAvailabilityView',
  EDIT_EXCLUSIVE_AVAILABILITY = 'ExclusiveAvailabilityEdit',
  EDIT_RESERVE_TIMESLOT_SETTINGS = 'AvailabilitySettingsEdit',
  ANNOUNCEMENT_EDIT = 'AnnouncementEdit',
  ANNOUNCEMENT_VIEW = 'AnnouncementView',
  BLOCKED_SLOT_VIEW = 'BlockSlotView',
  BLOCKED_SLOT_EDIT = 'BlockSlotEdit',
  VIEW_ADMIN = 'ViewAdmin',
  CREATE_ADMIN = 'CreateAdmin',
  VIEW_DEPARTMENT = 'ViewDepartment',
  EDIT_GRANT_DEPARTMENT_ADMIN = 'EditGrantDepartmentAdmin',
  EDIT_GRANT_FRANCHISE_ADMIN = 'EditGrantFranchiseAdmin',
  EDIT_GRANT_SCHEDULE_ADMIN = 'EditGrantScheduleAdmin',
  EDIT_GRANT_OFFLINE_DEPARTMENT_ADMIN = 'EditGrantOfflineDepartmentAdmin',
  ALLOCATION_SLOT_VIEW = 'AllocationSlotView',
  LESSON_DEFINITION_SAVE = 'LessonDefinitionSave',
  DISTRIBUTRON_RULE_VIEW = 'DistributronRuleView',
  SKILL_CREATE = 'SkillCreate',
  VIEW_OFFLINE_CLASS_ADMIN_PORTAL = 'ViewOfflineClassAdminPortal',
  CREATE_SCHOOL = 'CreateSchool',
}

const _PermissionValues: Permissions[] = Object.values(Permissions);
export type PermissionTypes = (typeof _PermissionValues)[number];

export enum Resources {
  TEACHER = 'teacher',
  HIERARCHY = 'hierarchy',
}

const _ResourceValues: Resources[] = Object.values(Resources);
export type ResourceTypes = (typeof _ResourceValues)[number];

export interface ITeacherProfile {
  id: string;
  firstName: string;
  lastName: string;
  department: {
    id: string;
    name: string;
    school: {
      id: string;
      name: string;
    };
  };
}

export interface GqlTeacherProfile {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  emailSubscribed: boolean;
  department: {
    id: string;
    name: string;
    school: School;
  };
  personalInterests: PersonalInterest[];
  instructionLanguages: string[];
  teacherSkills: TeacherSkill[];
  subjects: Subject[] | null;
  status: TeacherStatus;
  profilePicturePath: string;
  terminationDate: string;
  downgradeDate: string;
  path: string;
  role: TeacherRole;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
  permissions: IGrantedPermission[];
}

export interface GqlTeacherSearch {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  personalInterests: PersonalInterest[];
  instructionLanguages: string[];
  emailSubscribed: boolean;
  role: TeacherRole;
  teacherSkills: TeacherSkill[];
  profilePicturePath: string;
  department: {
    id: string;
    name: string;
    school: School;
  };
  path: string;
  bookings?: Booking[];
  availabilityBatch?: ITeacherAvailabilityRange;
}

export interface TeacherSkill {
  id: string;
}

export interface EvcProxyTunnel {
  description: string;
  domain: string;
  id: string;
  location: string;
}

export interface EvcTeacherSettings {
  teacherId: string;
  evcChinaTunnel: string;
  teacherTunnels: EvcTeacherTunnel[];
}

export interface EvcTeacherTunnel {
  id: string;
  location: EVCLocation;
}

export interface CreateTeacherRequest {
  firstName: string;
  lastName: string;
  email: string;
  departmentId: string;
  role?: TeacherRole;
  skillIds?: string[];
  subjectIds?: string[];
  instructionLanguages?: string[];
  personalInterests?: PersonalInterest[];
  emailSubscribed?: boolean;
}

export interface AddTeacherRequestValidation {
  firstName?: boolean;
  lastName?: boolean;
  email?: boolean;
  departmentId?: boolean;
}
